import { BookNowStatuses, CCFModalOptions, CCFModalSlugs, NeedHelpModalOptions } from 'Douleutaras/types/index';
import React from 'react';
import { Trans } from 'react-i18next';

export enum NEED_HELP_MILESTONES {
  INITIAL_CONTACT_MILESTONE = 'initial_contact_milestone',
  INTRODUCTORY_MEETING_MILESTONE = 'introductory_meeting_milestone',
  OFFER_MILESTONE = 'offer_milestone',
  START_THE_JOB_MILESTONE = 'start_the_job_milestone',
  COMPLETE_THE_JOB_MILESTONE = 'complete_the_job_milestone'
}

// Used in tracking
export const MILESTONE_NUMBER_TO_TRACKING_STRING = {
  2: 'Initial Contact',
  3: 'Introductory Appointment',
  4: 'Quote',
  5: 'Start the Job',
  6: 'Completion'
};

// Each step opens its own set of options
export const MODAL_BY_MILESTONE_POSITION = {
  2: NEED_HELP_MILESTONES.INITIAL_CONTACT_MILESTONE,
  3: NEED_HELP_MILESTONES.INTRODUCTORY_MEETING_MILESTONE,
  4: NEED_HELP_MILESTONES.OFFER_MILESTONE,
  5: NEED_HELP_MILESTONES.START_THE_JOB_MILESTONE,
  6: NEED_HELP_MILESTONES.COMPLETE_THE_JOB_MILESTONE
};

// Maps milestones to specific BookNow status. Used in "Update Status CCF" project
export const MILESTONE_TO_INDEX = {
  2: BookNowStatuses.ACTIVE,
  3: BookNowStatuses.MEETING_SCHEDULED,
  4: BookNowStatuses.QUOTE_SENT,
  5: BookNowStatuses.QUOTE_ACCEPTED,
  6: BookNowStatuses.COMPLETED
};

interface BidMilestones {
  [key: string]: {
    title: React.ReactElement,
    description: React.ReactElement,
    step: number
  };
}

export const BID_MILESTONES: BidMilestones = {
  [NEED_HELP_MILESTONES.INITIAL_CONTACT_MILESTONE]: {
    title: <Trans>Contact</Trans>,
    description: <Trans>CCF.BID MILESTONES.INITIAL CONTACT.DESCRIPTION</Trans>,
    step: 2
  },
  [NEED_HELP_MILESTONES.INTRODUCTORY_MEETING_MILESTONE]: {
    title: <Trans>Meeting</Trans>,
    description: <Trans>CCF.BID MILESTONES.MEETING.DESCRIPTION</Trans>,
    step: 3
  },
  [NEED_HELP_MILESTONES.OFFER_MILESTONE]: {
    title: <Trans>Quote</Trans>,
    description: <Trans>CCF.BID MILESTONES.OFFER.DESCRIPTION</Trans>,
    step: 4
  },
  [NEED_HELP_MILESTONES.START_THE_JOB_MILESTONE]: {
    title: <Trans>Start</Trans>,
    description: <Trans>CCF.BID MILESTONES.START JOB.DESCRIPTION</Trans>,
    step: 5
  },
  [NEED_HELP_MILESTONES.COMPLETE_THE_JOB_MILESTONE]: {
    title: <Trans>T.CD.BID-CARD.COMPLETED</Trans>,
    description: <Trans>CCF.BID MILESTONES.COMPLETE JOB.DESCRIPTION</Trans>,
    step: 6
  }
};

export type BidsMilestoneConfiguration = {
  [index in NEED_HELP_MILESTONES]: BidMilestoneConfiguration
};
export interface BidMilestoneConfiguration {
    enabledMilestones: Array<NEED_HELP_MILESTONES>; // Which milestones are available to be selected when current milestone is active
}

// Allowed milestones when current job is at least on 5th
export const BID_MILESTONES_ALLOW_NON_STARTED: BidMilestoneConfiguration = {
  enabledMilestones: [
    NEED_HELP_MILESTONES.INITIAL_CONTACT_MILESTONE,
    NEED_HELP_MILESTONES.INTRODUCTORY_MEETING_MILESTONE,
    NEED_HELP_MILESTONES.OFFER_MILESTONE
  ]
};

// Configuration of Bid Milestones. Used mainly in CCF Buyer Update actions
export const BID_MILESTONES_SELECT_CONFIGURATION: BidsMilestoneConfiguration = {
  [NEED_HELP_MILESTONES.INITIAL_CONTACT_MILESTONE]: {
    enabledMilestones: [
      NEED_HELP_MILESTONES.INITIAL_CONTACT_MILESTONE,
      NEED_HELP_MILESTONES.INTRODUCTORY_MEETING_MILESTONE,
      NEED_HELP_MILESTONES.OFFER_MILESTONE,
      NEED_HELP_MILESTONES.START_THE_JOB_MILESTONE,
      NEED_HELP_MILESTONES.COMPLETE_THE_JOB_MILESTONE
    ]
  },
  [NEED_HELP_MILESTONES.INTRODUCTORY_MEETING_MILESTONE]: {
    enabledMilestones: [
      NEED_HELP_MILESTONES.INITIAL_CONTACT_MILESTONE,
      NEED_HELP_MILESTONES.INTRODUCTORY_MEETING_MILESTONE,
      NEED_HELP_MILESTONES.OFFER_MILESTONE,
      NEED_HELP_MILESTONES.START_THE_JOB_MILESTONE,
      NEED_HELP_MILESTONES.COMPLETE_THE_JOB_MILESTONE
    ]
  },
  [NEED_HELP_MILESTONES.OFFER_MILESTONE]: {
    enabledMilestones: [
      NEED_HELP_MILESTONES.INITIAL_CONTACT_MILESTONE,
      NEED_HELP_MILESTONES.INTRODUCTORY_MEETING_MILESTONE,
      NEED_HELP_MILESTONES.OFFER_MILESTONE,
      NEED_HELP_MILESTONES.START_THE_JOB_MILESTONE,
      NEED_HELP_MILESTONES.COMPLETE_THE_JOB_MILESTONE
    ]
  },
  [NEED_HELP_MILESTONES.START_THE_JOB_MILESTONE]: {
    enabledMilestones: [
      NEED_HELP_MILESTONES.START_THE_JOB_MILESTONE,
      NEED_HELP_MILESTONES.COMPLETE_THE_JOB_MILESTONE
    ]
  },
  [NEED_HELP_MILESTONES.COMPLETE_THE_JOB_MILESTONE]: {
    enabledMilestones: [
      NEED_HELP_MILESTONES.COMPLETE_THE_JOB_MILESTONE
    ]
  }
};

export interface SelectedModalArgs {
  optionSlug?: NeedHelpModalOptions | AllModalOptions;
  modalSlug: CCFModalSlugs;
  modalOptions: CCFModalOptions;
  extraModalOptions?: { [s: string]: string }; // Custom options coming from FE
}

export enum ContactModalOptions {
  I_WILL_SEND_ONLINE_PAYMENT = 'i_will_send_online_payment',
  I_WILL_CALL_THE_PRO = 'i_will_call_the_pro',
  I_WILL_USE_THE_CHAT = 'i_will_use_the_chat',
  I_WANT_EXTRA_PRO = 'i_want_extra_pro'
}

export enum SelectionContactModalOptions {
  WITH_PRO = 'with_pro',
  WITH_NONE = 'with_none'
}

export enum SolveIssueModalOptions {
  I_HAVE_AN_ISSUE = 'i_have_an_issue',
  I_WANT_TO_PROCEED_HAVE_PROCEEDED = 'i_want_to_proceed_have_proceeded',
  I_WONT_PROCEED_WITH_THE_JOB = 'i_wont_proceed_with_the_job'
}

export enum ExtraBidModalOptions {
  I_WILL_PROCEED_WITH_A_PRO = 'i_will_proceed_with_a_pro',
  I_NEED_AN_EXTRA_PRO = 'i_need_an_extra_pro'
}

export type AllModalOptions = ExtraBidModalOptions | ContactModalOptions | NeedHelpModalOptions | SelectionContactModalOptions
  | SolveIssueModalOptions;

export const NeedHelpModalOptionsTranslated = {
  pro_not_in_touch: <Trans>T.CCF.NEED HELP.OPTIONS.pro-not-in-touch</Trans>,
  i_will_not_proceed: <Trans>T.CCF.NEED HELP.OPTIONS.i-will-not-proceed</Trans>,
  pro_out_of_service_area: <Trans>T.CCF.NEED HELP.OPTIONS.pro-out-of-service-area</Trans>,
  something_else: <Trans>T.CCF.NEED HELP.OPTIONS.something-else</Trans>,
  pro_not_showed_in_meeting: <Trans>T.CCF.NEED HELP.OPTIONS.pro-not-showed-in-meeting</Trans>,
  pros_not_showed_in_meeting: <Trans>T.CCF.NEED HELP.OPTIONS.pros-not-showed-in-meeting</Trans>,
  pro_no_attitude_or_skills_trust: <Trans>T.CCF.NEED HELP.OPTIONS.pro-no-attitude-or-skills-trust</Trans>,
  pros_no_attitude_or_skills_trust: <Trans>T.CCF.NEED HELP.OPTIONS.pros-no-attitude-or-skills-trust</Trans>,
  i_want_extra_pro: <Trans>T.CCF.NEED HELP.OPTIONS.i-want-extra-pro</Trans>,
  i_want_to_correct_my_job_status: <Trans>T.CCF.NEED HELP.OPTIONS.i-want-to-correct-my-job-status</Trans>,
  quote_out_of_budget: <Trans>T.CCF.NEED HELP.OPTIONS.quote-out-of-budget</Trans>,
  pro_does_not_satisfy_my_reqs: <Trans>T.CCF.NEED HELP.OPTIONS.pro-does-not-satisfy-my-reqs</Trans>,
  pros_do_not_satisfy_my_reqs: <Trans>T.CCF.NEED HELP.OPTIONS.pros-do-not-satisfy-my-reqs</Trans>,
  i_will_not_proceed_due_to_various_reasons: <Trans>T.CCF.NEED HELP.OPTIONS.i-will-not-proceed-due-to-various-reasons</Trans>,
  job_started_concerns_about_quality: <Trans>T.CCF.NEED HELP.OPTIONS.job-started-concerns-about-quality</Trans>,
  job_not_started_pro_to_blame: <Trans>T.CCF.NEED HELP.OPTIONS.job-not-started-pro-to-blame</Trans>,
  pro_not_completed_the_job: <Trans>T.CCF.NEED HELP.OPTIONS.pro-not-completed-the-job</Trans>,
  not_proceeded_with_specific_pro_and_need_new_one: <Trans>T.CCF.NEED HELP.OPTIONS.not-proceeded-with-specific-pro-and-need-new-one</Trans>,
  i_wont_proceed_with_the_job: <Trans>T.CCF.NEED HELP.OPTIONS.i-wont-proceed-with-the-job</Trans>,
  pro_cannot_do_the_job: <Trans>T.CCF.NEED HELP.OPTIONS.pro-cannot-do-the-job</Trans>,
  pros_not_in_touch: <Trans>T.CCF.NEED HELP.OPTIONS.pros-not-in-touch</Trans>,
  pros_cannot_do_the_job: <Trans>T.CCF.NEED HELP.OPTIONS.pros-cannot-do-the-job</Trans>
};

export const WhatHappenedModalTranslated = {
  looking_for_other_profession: <Trans>T.CCF.WHAT HAPPENED.OPTIONS.looking-for-other-profession</Trans>,
  something_else: <Trans>T.CCF.WHAT HAPPENED.OPTIONS.something-else</Trans>,
  suggested_pro_does_not_cover_me_i_need_an_extra_pro:
    <Trans>T.CCF.WHAT HAPPENED.OPTIONS.suggested-pro-does-not-cover-me-i-need-an-extra-pro</Trans>,
  suggested_pros_do_not_cover_me: <Trans>T.CCF.WHAT HAPPENED.OPTIONS.suggested-pros-do-not-cover-me</Trans>
};

export const BOOK_NOW_STATUS_TO_MILESTONE_STEP = {
  [BookNowStatuses.JOB_POST]: 2,
  [BookNowStatuses.ACTIVE]: 2,
  [BookNowStatuses.MEETING_NOT_SCHEDULED]: 2,
  [BookNowStatuses.MEETING_SCHEDULED]: 3,
  [BookNowStatuses.MEETING_DONE]: 3,
  [BookNowStatuses.TO_FOLLOW_UP]: 3,
  [BookNowStatuses.MEETING_NOT_DONE]: 3,
  [BookNowStatuses.QUOTE_NOT_SENT]: 3,
  [BookNowStatuses.QUOTE_NOT_ACCEPTED]: 4,
  [BookNowStatuses.QUOTE_CUSTOMER_THINKING]: 4,
  [BookNowStatuses.QUOTE_SENT]: 4,
  [BookNowStatuses.QUOTE_ACCEPTED]: 5,
  [BookNowStatuses.JOB_STARTED]: 5,
  [BookNowStatuses.JOB_WILL_NOT_START]: 5,
  [BookNowStatuses.CANNOT_BE_COMPLETED]: 5,
  [BookNowStatuses.COMPLETION_PENDING]: 5,
  [BookNowStatuses.COMPLETED]: 6
};

export const BOOK_NOW_STATUS_TO_INDEX = {
  [BookNowStatuses.ACTIVE]: 0,
  [BookNowStatuses.MEETING_SCHEDULED]: 1,
  [BookNowStatuses.QUOTE_NOT_SENT]: 2,
  [BookNowStatuses.MEETING_NOT_SCHEDULED]: 3,
  [BookNowStatuses.MEETING_NOT_DONE]: 4,
  [BookNowStatuses.TO_FOLLOW_UP]: 5,
  [BookNowStatuses.MEETING_DONE]: 6,
  [BookNowStatuses.QUOTE_SENT]: 7,
  [BookNowStatuses.QUOTE_NOT_ACCEPTED]: 8,
  [BookNowStatuses.QUOTE_CUSTOMER_THINKING]: 9,
  [BookNowStatuses.QUOTE_ACCEPTED]: 10,
  [BookNowStatuses.JOB_WILL_NOT_START]: 11,
  [BookNowStatuses.JOB_STARTED]: 12,
  [BookNowStatuses.CANNOT_BE_COMPLETED]: 13,
  [BookNowStatuses.COMPLETED]: 14,
  [BookNowStatuses.COMPLETION_PENDING]: 15
};

export const BOOK_NOW_INDEXES = [
  {
    status: BookNowStatuses.ACTIVE,
    index: BOOK_NOW_STATUS_TO_INDEX[BookNowStatuses.ACTIVE]
  },
  {
    status: BookNowStatuses.MEETING_SCHEDULED,
    index: BOOK_NOW_STATUS_TO_INDEX[BookNowStatuses.MEETING_SCHEDULED]
  },
  {
    status: BookNowStatuses.QUOTE_NOT_SENT,
    index: BOOK_NOW_STATUS_TO_INDEX[BookNowStatuses.QUOTE_NOT_SENT]
  },
  {
    status: BookNowStatuses.MEETING_NOT_SCHEDULED,
    index: BOOK_NOW_STATUS_TO_INDEX[BookNowStatuses.MEETING_NOT_SCHEDULED]
  },
  {
    status: BookNowStatuses.MEETING_NOT_DONE,
    index: BOOK_NOW_STATUS_TO_INDEX[BookNowStatuses.MEETING_NOT_DONE]
  },
  {
    status: BookNowStatuses.TO_FOLLOW_UP,
    index: BOOK_NOW_STATUS_TO_INDEX[BookNowStatuses.TO_FOLLOW_UP]
  },
  {
    status: BookNowStatuses.MEETING_DONE,
    index: BOOK_NOW_STATUS_TO_INDEX[BookNowStatuses.MEETING_DONE]
  },
  {
    status: BookNowStatuses.QUOTE_SENT,
    index: BOOK_NOW_STATUS_TO_INDEX[BookNowStatuses.QUOTE_SENT]
  },
  {
    status: BookNowStatuses.QUOTE_NOT_ACCEPTED,
    index: BOOK_NOW_STATUS_TO_INDEX[BookNowStatuses.QUOTE_NOT_ACCEPTED]
  },
  {
    status: BookNowStatuses.QUOTE_CUSTOMER_THINKING,
    index: BOOK_NOW_STATUS_TO_INDEX[BookNowStatuses.QUOTE_CUSTOMER_THINKING]
  },
  {
    status: BookNowStatuses.QUOTE_ACCEPTED,
    index: BOOK_NOW_STATUS_TO_INDEX[BookNowStatuses.QUOTE_ACCEPTED]
  },
  {
    status: BookNowStatuses.JOB_WILL_NOT_START,
    index: BOOK_NOW_STATUS_TO_INDEX[BookNowStatuses.JOB_WILL_NOT_START]
  },
  {
    status: BookNowStatuses.JOB_STARTED,
    index: BOOK_NOW_STATUS_TO_INDEX[BookNowStatuses.JOB_STARTED]
  },
  {
    status: BookNowStatuses.CANNOT_BE_COMPLETED,
    index: BOOK_NOW_STATUS_TO_INDEX[BookNowStatuses.CANNOT_BE_COMPLETED]
  },
  {
    status: BookNowStatuses.COMPLETED,
    index: BOOK_NOW_STATUS_TO_INDEX[BookNowStatuses.COMPLETED]
  },
  {
    status: BookNowStatuses.COMPLETION_PENDING,
    index: BOOK_NOW_STATUS_TO_INDEX[BookNowStatuses.COMPLETION_PENDING]
  }
];
